import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { render } from "react-dom";

import "./style.css";

const BASE_URL = (window.CARBONE_API_BASE || "https://carbone-be.agsquared.com").replace(/\/$/, "");

function App() {
  const [mode, setMode] = useState("GENERATE"); // GENERATE | UPLOAD | IMPORT_ID | IMPORT_URL
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [jsonFile, setJsonFile] = useState(null);
  const [uploadTemplateFile, setUploadTemplateFile] = useState(null);
  const [convertTo, setConvertTo] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [cloudStatus, setCloudStatus] = useState(null);
  const [importTemplateId, setImportTemplateId] = useState("");
  const [importFilename, setImportFilename] = useState("");
  const [importUrl, setImportUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const loadTemplates = useCallback(async () => {
    try {
      setTemplatesLoading(true);
      setErrorMsg("");
      const res = await fetch(`${BASE_URL}/templates`);
      if (!res.ok) throw new Error(`Failed to load templates (${res.status})`);
      const list = await res.json();
      const normalized = Array.isArray(list) ? list : [];
      setTemplates(normalized);
    } catch (e) {
      setErrorMsg(e.message || String(e));
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const loadCloudStatus = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/carbone/status`);
      if (!res.ok) throw new Error(`Failed to load status`);
      const status = await res.json();
      setCloudStatus(status);
    } catch (e) {
      setCloudStatus(null);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadCloudStatus();
  }, [loadTemplates, loadCloudStatus]);

  const handleGenerate = useCallback(async () => {
    if (!selectedTemplate) {
      setErrorMsg("Please select a template");
      return;
    }
    if (!jsonFile) {
      setErrorMsg("Please choose a JSON data file");
      return;
    }
    try {
      setLoading(true);
      setErrorMsg("");
      const text = await jsonFile.text();
      let data;
      try { data = JSON.parse(text); } catch (e) { throw new Error("Invalid JSON file"); }
      const body = JSON.stringify({ templateName: selectedTemplate, data, convertTo, outputLink: false });
      const res = await fetch(`${BASE_URL}/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Generate failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const base = selectedTemplate.replace(/\.docx$/i, "");
      const name = `result_${base}${convertTo ? "." + convertTo : ""}` || "result_output";
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setErrorMsg(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate, jsonFile, convertTo]);

  const handleUploadTemplate = useCallback(async () => {
    if (!uploadTemplateFile) {
      setErrorMsg("Please choose a DOCX template to upload");
      return;
    }
    try {
      setLoading(true);
      setErrorMsg("");
      const fd = new FormData();
      fd.append("template", uploadTemplateFile);
      const res = await fetch(`${BASE_URL}/templates/upload`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const info = await res.json().catch(() => ({}));
      await loadTemplates();
      if (info && info.name) {
        setMode("GENERATE");
        setSelectedTemplate(info.name);
      }
      setUploadTemplateFile(null);
      alert(`Template uploaded successfully${info.templateId ? '\nCloud ID: ' + info.templateId : ''}`);
    } catch (e) {
      setErrorMsg(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [uploadTemplateFile, loadTemplates]);

  const handleImportById = useCallback(async () => {
    if (!importTemplateId || !importTemplateId.trim()) {
      setErrorMsg("Please enter a Carbone Template ID");
      return;
    }
    try {
      setLoading(true);
      setErrorMsg("");
      const body = JSON.stringify({ templateId: importTemplateId.trim(), filename: importFilename.trim() || undefined });
      const res = await fetch(`${BASE_URL}/templates/import-id`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Import failed (${res.status})`);
      }
      const info = await res.json();
      await loadTemplates();
      setImportTemplateId("");
      setImportFilename("");
      setMode("GENERATE");
      setSelectedTemplate(info.name);
      alert(`Template imported successfully: ${info.name}`);
    } catch (e) {
      setErrorMsg(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [importTemplateId, importFilename, loadTemplates]);

  const handleImportByUrl = useCallback(async () => {
    if (!importUrl || !importUrl.trim()) {
      setErrorMsg("Please enter a URL");
      return;
    }
    try {
      setLoading(true);
      setErrorMsg("");
      const body = JSON.stringify({ url: importUrl.trim(), filename: importFilename.trim() || undefined });
      const res = await fetch(`${BASE_URL}/templates/import-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Import failed (${res.status})`);
      }
      const info = await res.json();
      await loadTemplates();
      setImportUrl("");
      setImportFilename("");
      setMode("GENERATE");
      setSelectedTemplate(info.name);
      alert(`Template imported successfully: ${info.name}`);
    } catch (e) {
      setErrorMsg(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [importUrl, importFilename, loadTemplates]);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(t => (t.name || t).toLowerCase().includes(q));
  }, [templates, searchQuery]);

  const templateOptions = useMemo(() => filteredTemplates.map((t) => (
    <MenuItem key={t.name || t} value={t.name || t}>
      <span style={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: '8px'}}>
        <span style={{flex: 1}}>{t.name || t}</span>
        {t.templateId && <span style={{background: '#10b981', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px'}}>☁</span>}
        <small style={{color: '#6b7280', fontSize: '11px'}}>{t.mtime ? new Date(t.mtime).toLocaleDateString() : ''}</small>
      </span>
    </MenuItem>
  )), [filteredTemplates]);

  return (
    <div className="container">
      {loading && <div className="overlay"><CircularProgress /></div>}

      <div className="header">
        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          <h2 style={{margin: 0}}>Carbone Report Tool</h2>
          {cloudStatus && (
            <span style={{background: '#10b981', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px'}}>
              ☁ Cloud Connected ({cloudStatus.templates} templates)
            </span>
          )}
        </div>
        <div className="mode-toggle">
          <Button onClick={() => setMode("GENERATE")} variant={mode === "GENERATE" ? "contained" : "outlined"} color="primary" size="small">Generate</Button>
          <Button onClick={() => setMode("UPLOAD")} variant={mode === "UPLOAD" ? "contained" : "outlined"} color="primary" size="small" style={{marginLeft: 4}}>Upload</Button>
          <Button onClick={() => setMode("IMPORT_ID")} variant={mode === "IMPORT_ID" ? "contained" : "outlined"} color="primary" size="small" style={{marginLeft: 4}}>Import ID</Button>
          <Button onClick={() => setMode("IMPORT_URL")} variant={mode === "IMPORT_URL" ? "contained" : "outlined"} color="primary" size="small" style={{marginLeft: 4}}>Import URL</Button>
        </div>
      </div>

      {errorMsg && <div className="error-box">{errorMsg}</div>}

      <div className="card">
        {mode === "GENERATE" && (
          <div className="grid">
            <div className="field">
              <InputLabel shrink>Template</InputLabel>
              <Input 
                placeholder="Search templates..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{marginBottom: 8}}
              />
              <FormControl fullWidth>
                <Select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} displayEmpty>
                  <MenuItem value="" disabled>{templatesLoading ? "Loading templates..." : (filteredTemplates.length ? "Select a template" : searchQuery ? "No matches" : "No templates found")}</MenuItem>
                  {templateOptions}
                </Select>
              </FormControl>
              <div style={{display: 'flex', gap: '8px', marginTop: 8}}>
                <Button onClick={loadTemplates} size="small">Refresh</Button>
                {selectedTemplate && templates.find(t => t.name === selectedTemplate)?.templateId && (
                  <small style={{color: '#6b7280', alignSelf: 'center'}}>Cloud ID: {templates.find(t => t.name === selectedTemplate).templateId.substring(0, 12)}...</small>
                )}
              </div>
            </div>

            <div className="field">
              <InputLabel shrink>Data (JSON)</InputLabel>
              <Input type="file" inputProps={{accept: ".json,application/json"}} onChange={(e) => setJsonFile(e.target.files && e.target.files[0])} />
            </div>

            <div className="field">
              <InputLabel shrink>Convert To</InputLabel>
              <FormControl fullWidth>
                <Select value={convertTo} onChange={(e) => setConvertTo(e.target.value)}>
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="pdf">pdf</MenuItem>
                  <MenuItem value="docx">docx</MenuItem>
                  <MenuItem value="txt">txt</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className="actions">
              <Button variant="contained" color="primary" onClick={handleGenerate}>Generate</Button>
            </div>
          </div>
        )}

        {mode === "UPLOAD" && (
          <div className="grid">
            <div className="field">
              <InputLabel shrink>Template (DOCX)</InputLabel>
              <Input type="file" inputProps={{accept: ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"}} onChange={(e) => setUploadTemplateFile(e.target.files && e.target.files[0])} />
              <small style={{color: '#6b7280', marginTop: 4}}>File will be saved locally and mirrored to Carbone Cloud</small>
            </div>
            <div className="actions">
              <Button variant="contained" color="primary" onClick={handleUploadTemplate}>Upload & Mirror</Button>
            </div>
          </div>
        )}

        {mode === "IMPORT_ID" && (
          <div className="grid">
            <div className="field">
              <InputLabel shrink>Carbone Template ID</InputLabel>
              <Input 
                placeholder="e.g., 74dcb3335a8ae8c1c7e401383b..." 
                value={importTemplateId} 
                onChange={(e) => setImportTemplateId(e.target.value)}
                fullWidth
              />
              <small style={{color: '#6b7280', marginTop: 4}}>Get template ID from Carbone Cloud dashboard</small>
            </div>
            <div className="field">
              <InputLabel shrink>Local Filename (optional)</InputLabel>
              <Input 
                placeholder="e.g., my_template.docx" 
                value={importFilename} 
                onChange={(e) => setImportFilename(e.target.value)}
                fullWidth
              />
              <small style={{color: '#6b7280', marginTop: 4}}>Leave empty to use cloud filename</small>
            </div>
            <div className="actions">
              <Button variant="contained" color="primary" onClick={handleImportById}>Import from Cloud</Button>
            </div>
          </div>
        )}

        {mode === "IMPORT_URL" && (
          <div className="grid">
            <div className="field">
              <InputLabel shrink>Template URL</InputLabel>
              <Input 
                placeholder="https://example.com/template.docx" 
                value={importUrl} 
                onChange={(e) => setImportUrl(e.target.value)}
                fullWidth
              />
              <small style={{color: '#6b7280', marginTop: 4}}>URL must be publicly accessible</small>
            </div>
            <div className="field">
              <InputLabel shrink>Local Filename (optional)</InputLabel>
              <Input 
                placeholder="e.g., my_template.docx" 
                value={importFilename} 
                onChange={(e) => setImportFilename(e.target.value)}
                fullWidth
              />
              <small style={{color: '#6b7280', marginTop: 4}}>Leave empty to use URL filename</small>
            </div>
            <div className="actions">
              <Button variant="contained" color="primary" onClick={handleImportByUrl}>Import & Mirror</Button>
            </div>
          </div>
        )}
      </div>

      <div className="footer">
        <small>Backend: {BASE_URL}</small>
      </div>
    </div>
  );
}

render(<App />, document.getElementById("root"));
